
import { ExpandMore } from "@mui/icons-material";
import { Box, Breadcrumbs, IconButton, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { subscribe, unsubscribe } from 'pubsub-js';
import React, { useCallback, useEffect, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { useLocation  } from 'react-router';
import { NavigationSource } from '../../common/enums/NavigationSource';
import { UIViewEnum } from '../../common/enums/UIViewEnum';
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { OBJECT_CHANGED_TOPIC, ObjectChangedMsg } from '../../common/models/pub-sub/PubSubTopics';
import { getDeviceRoute, getGroupRoute, getParentViewIDFromPath, getTagRoute, getTankBatteryRoute, getTankRoute, getViewIDFromPath, getWellRoute } from '../../common/utilities/RouteHelpers';
import { LayoutContextActionType, SwipeActionEnum } from "../../contexts/layout-context/LayoutContextTypes";
import { useLayoutContext } from "../../contexts/layout-context/UseLayoutContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from '../../contexts/user-context/UserContext';
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from '../../queries/UseQueryNotifications';
import { useBreadcrumb } from '../../queries/breadcrumb/UseBreadcrumb';
import { useUpdateBreadCrumb } from '../../queries/breadcrumb/UseUpdateBreadcrumb';
import { INavigationItem } from '../../services/navigation-data-service/Models';
import BreadCrumbItem from "./BreadCrumbItem";
import useNavigateTo, { NavigateToOptions } from "../../hooks/use-navigate-to/useNavigateTo";
import { NewViewTypeEnum } from "../../common/enums/NewViewTypeEnum";

export type BreadCrumbProps = {
    readOnly?: boolean;
};

export default function BreadCrumb({ readOnly }: BreadCrumbProps) {

    const [contextObjectName, setContextObjectName] = useState<string | undefined>();
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [layoutState, layoutDispatch] = useLayoutContext();
    const location = useLocation();
    const navigateTo = useNavigateTo();
    const [offlineState] = useOfflineDetectContext();
    const theme = useTheme();

    //console.log(`isMobile: ${isMobile} isTablet: ${isTablet}`);
    
    const isPhone = isMobile && !isTablet;

    // use the parent viewID to prevent each child view from caching the same data for the breadcrum
    // which results in multiple calls to the backend for the same data.
    //const viewID = getViewIDFromPath(location.pathname, UIViewEnum.Group_DeviceSummary);
    const viewID = getParentViewIDFromPath(location.pathname, UIViewEnum.Group_SummaryListing_Devices);

    const { data: response, refetch } = useQueryNotifications(useBreadcrumb)({
        customerID: userState.customer?.id as number,
        viewID: viewID,
        routeContext: routeContext,
        offlineDataLoaded: offlineState.offlineDataLoaded,
        offline: offlineState.offline
        }
    );    

    const {mutate: updateBreadCrumb} = useQueryNotifications(useUpdateBreadCrumb)({
        customerID: userState.customer?.id as number,
        viewID: viewID,
        routeContext: routeContext,
        offlineDataLoaded: offlineState.offlineDataLoaded,
        offline: offlineState.offline
    });


    const [anchorElement, setAnchorElement] = useState<HTMLButtonElement>();
    const open = Boolean(anchorElement);


    const breadcrumbItem = response?.breadcrumbItems.find(o => o.objectID === routeContext.contextObject?.id && o.objectTypeID === routeContext.contextObject?.objectType);

    const navigateToItem = useCallback((item: INavigationItem, navigationSource: NavigationSource, options?: NavigateToOptions) => {

        // @@TODO when redirect is available there is specific logic to set path to redirect for sitelisting
        // because some groups are not configured for site listing 

        if (item.allowClick) {
            let url: string | undefined;

            let currentViewID: UIViewEnum;

            switch (item.objectTypeID) {
                case eLynxObjectTypeEnum.Group:
                    currentViewID = routeContext?.contextObject?.objectType === eLynxObjectTypeEnum.Group ?
                        getViewIDFromPath(location.pathname, UIViewEnum.Group_SummaryListing) :
                        UIViewEnum.Group_SummaryListing;

                    // if (!response?.validGroupViews.find(o => o.Key === item.objectID)?.Value?.find(o => o === currentViewID)) {
                    //     currentViewID = UIViewEnum.Group_SummaryListing;
                    // }

                    url = getGroupRoute(currentViewID, item.objectID);

                    break;

                case eLynxObjectTypeEnum.Device:
                    currentViewID = routeContext?.contextObject?.objectType === eLynxObjectTypeEnum.Device ?
                        getViewIDFromPath(location.pathname, UIViewEnum.Device_Details) :
                        UIViewEnum.Device_Details;
                    url = getDeviceRoute(currentViewID, routeContext.groupID, routeContext.wellID, item.objectID, isPhone);

                    break;

                case eLynxObjectTypeEnum.Well:
                    currentViewID = routeContext?.contextObject?.objectType === eLynxObjectTypeEnum.Well ?
                        getViewIDFromPath(location.pathname, UIViewEnum.Well_Details) :
                        UIViewEnum.Well_Details;
                    url = getWellRoute(currentViewID, routeContext.groupID, item.objectID);
                    break;

                case eLynxObjectTypeEnum.Tank:
                    currentViewID = routeContext?.contextObject?.objectType === eLynxObjectTypeEnum.Tank ?
                        getViewIDFromPath(location.pathname, UIViewEnum.Tank_Details) :
                        UIViewEnum.Tank_Details;
                    url = getTankRoute(currentViewID, routeContext.groupID, routeContext.deviceID, routeContext.tankBatteryID, item.objectID);
                    break;

                case eLynxObjectTypeEnum.TankBattery:
                    currentViewID = routeContext?.contextObject?.objectType === eLynxObjectTypeEnum.TankBattery ?
                        getViewIDFromPath(location.pathname, UIViewEnum.TankBattery_Details) :
                        UIViewEnum.TankBattery_Details;
                    url = getTankBatteryRoute(currentViewID, routeContext.groupID, item.objectID);
                    break;

                case eLynxObjectTypeEnum.Tag:
                    currentViewID = routeContext?.contextObject?.objectType === eLynxObjectTypeEnum.Tag ?
                        getViewIDFromPath(location.pathname, UIViewEnum.Tag_Details) :
                        UIViewEnum.Tag_Details;
                    url = getTagRoute(currentViewID, routeContext.groupID, routeContext.wellID, routeContext.deviceID, routeContext.tankBatteryID, routeContext.tankID, item.objectID);
                    break;

                default:
                    throw new Error(`Unable to navigate to object type: ${item.objectTypeID.toString()}`);
            }

	        url = `${url}&navSrc=${navigationSource}`

            if (!options?.newViewType) {
                setContextObjectName(item.title);                
            }
                
            navigateTo(url, options);
            
		}

    }, [
        navigateTo,
        location.pathname,
        routeContext,
        setContextObjectName,
        isPhone
    ]);

    const onAnchorClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        setAnchorElement(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorElement(undefined);
    };

    const onMenuItemClick = (item: INavigationItem, shiftKey: boolean, ctrlKey: boolean) => {
        setAnchorElement(undefined);

        if (item) {
            onSelected(item, shiftKey, ctrlKey);
        }
    };

    const onSelected = (item: INavigationItem, shiftKey: boolean, ctrlKey: boolean): void => {

        let options: NavigateToOptions | undefined;

        if (shiftKey || ctrlKey) {
            options = {
                newViewType: shiftKey ? NewViewTypeEnum.NewWindow : NewViewTypeEnum.NewTab
            }
        }

        navigateToItem(item, NavigationSource.Breadcrumb, options);
    };

    useEffect(() => {

        if (response && layoutState.swipeAction !== undefined &&
            layoutState.swipeAction !== SwipeActionEnum.None &&
            (response?.relatedItems.length ?? 0) > 1) {

            const ndx = response.relatedItems.findIndex(o => o.objectID === routeContext.contextObject?.id);
            let targetIndex: number = -1;

            if (layoutState.swipeAction === SwipeActionEnum.Left) {
                // next
                targetIndex = (ndx === response.relatedItems.length - 1 ? 0 : ndx + 1);

            } else if (layoutState.swipeAction === SwipeActionEnum.Right) {
                // previous
                targetIndex = (ndx === 0 ? response.relatedItems.length - 1 : ndx - 1);
            }

            navigateToItem(response.relatedItems[targetIndex], layoutState.swipeAction === SwipeActionEnum.Left ? NavigationSource.ArrowPrev : NavigationSource.ArrowNext);

            layoutDispatch({
                type: LayoutContextActionType.SwipeChanged,
                payload: undefined
            });

        }

    }, [layoutState.swipeAction,
        response,
        routeContext.contextObject,
        navigateToItem,
        layoutDispatch
    ]);

    useEffect(() => {
        if (response) {
            setContextObjectName(response.contextObjectName);
        }

    }, [response, setContextObjectName])


    useEffect(() => {
        const onObjectChanged = (objectChangedData: ObjectChangedMsg) => {

            const item = response?.breadcrumbItems.find(o => o.objectID === objectChangedData.entity.id && o.objectTypeID === objectChangedData.entity.objectType);

            if (item) {

                // the changed object is a group and the name is not set, then the change likely came from
                // group admin so rebuild the breadcrumb
                if (objectChangedData.entity.objectType === eLynxObjectTypeEnum.Group &&
                    objectChangedData.name === undefined) {

                    refetch();

                    return;
                }


                if (objectChangedData.name) {
                    item.title = objectChangedData.name;
                }

                if (objectChangedData.visible !== undefined) {
                    item.isHidden = objectChangedData.visible ? false : true;
                }

                if (objectChangedData.disabled !== undefined) {
                    item.isObjectDisabled = objectChangedData.disabled ? true : false;
                }

                updateBreadCrumb(item);                
            }
        }

        const token = subscribe(OBJECT_CHANGED_TOPIC, (message, data) => onObjectChanged(data));

        return () => {
            if (token) {
                unsubscribe(token);
            }
        }
    }, [
        response,
        updateBreadCrumb,
        refetch
    ]);

    return (
        <Box
            sx={{display: 'flex', 
            //height: theme => theme.spacing(3),
            //backgroundColor: isPhone ? 'rgba(41, 57, 85, 1)' : undefined, 
            flex: '1 1 auto',
            alignItems: 'center'}} >
            {/* desktop show breadcrumb */}
            {!isPhone &&

                <Breadcrumbs aria-label='breadcrumb' sx={{
                    fontSize: '.9rem',
                }} maxItems={!isPhone ? undefined : 2}>
                    {
                        response?.breadcrumbItems.map((item, index) => (

                            <BreadCrumbItem key={`${item.objectTypeID}:${item.objectID}`}
                                item={item}
                                showAnchor={(response?.relatedItems.length ?? 0) > 0 && index === (response?.breadcrumbItems.length - 1)}
                                onClick={onSelected}
                                onAnchorClick={onAnchorClick} />
                        ))
                    }
                </Breadcrumbs>
            }
            {/* mobile, no breadcrumb but related objects dropdown */}
            {isPhone && <Typography
                variant='h6'
                onClick={onAnchorClick}
                sx={{
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    marginLeft: theme.spacing(1),
                    color: '#fff',
                    textDecoration: breadcrumbItem?.isHidden ? 'line-through' : undefined,

                    //display: 'flex',
                    //flex: '1 1 auto',
                    fontStyle: breadcrumbItem?.isObjectDisabled ? 'italic' : undefined
                }}>
                {contextObjectName}
            </Typography>}
            {
                isPhone && (response?.relatedItems.length ?? 0) > 0 && !readOnly &&
                <IconButton
                    sx={{ color: '#fff' }}
                    aria-label="related"
                    aria-controls="related-items-menu"
                    aria-haspopup="true"
                    onClick={onAnchorClick}>
                    <ExpandMore />
                </IconButton>
            }
            {!readOnly &&
                <Menu
                    id="related-items-menu"
                    color='textSecondary'
                    anchorEl={anchorElement}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }
                    }
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center'
                    }
                    }
                    keepMounted
                    open={open}
                    onClose={handleClose}>
                    {response?.relatedItems.map((item) => (
                        <MenuItem key={`${item.objectTypeID}:${item.objectID}`}
                            sx={{
                                fontStyle: item.isObjectDisabled ? 'italic' : undefined,
                                textDecoration: item.isHidden ? 'line-through' : undefined,
                                "&:hover": {
                                    textDecoration: item.isHidden ? 'line-through' : undefined,
                                    cursor: "pointer"
                                }
                            }}
                            selected={item.objectID === routeContext?.contextObject?.id}
                            onClick={(ev) => onMenuItemClick(item, ev.shiftKey, ev.ctrlKey)}>
                            <Typography variant='inherit'>
                                {item.title}
                            </Typography>
                        </MenuItem>
                    ))}
                </Menu>
            }
        </Box>
    );
}
